import { useMemo } from 'react';

import { faArrowsToDot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import { useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { selectedVisitAtom } from '../../../app/appHeader/imageViewerMenu/atoms';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import type { BoundingBoxFinding } from '../types';

import { selectedFindingImageIdAtom, selectedFindingsAtom } from './atoms';

export const ShowOnlyFindingButton = ({
  finding,
  tooth,
  onClick,
}: {
  finding: BoundingBoxFinding;
  tooth?: number;
  onClick?: () => void;
}) => {
  const setSelectedFindingBounds = useSetAtom(selectedFindingsAtom);
  const setSelectedFindingImageId = useSetAtom(selectedFindingImageIdAtom);

  const selectedFindingBounds = useAtomValue(selectedFindingsAtom);
  const selectedFindingImageId = useAtomValue(selectedFindingImageIdAtom);
  const estimatedVisitId = useAtomValue(selectedVisitAtom);

  const { trackEvent } = useTrackEvent();
  const { id: patientId, imageId: currentImage } = useParams<{
    id: string;
    imageId?: string;
  }>();

  // Check if this finding is currently selected
  const isCurrentlySelected = useMemo(() => {
    return (
      selectedFindingBounds?.id === finding.id &&
      selectedFindingImageId === currentImage
    );
  }, [
    selectedFindingBounds?.id,
    selectedFindingImageId,
    finding.id,
    currentImage,
  ]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (isCurrentlySelected) {
      // Clear the selection
      setSelectedFindingBounds(undefined);
      setSelectedFindingImageId(undefined);
      trackEvent(mixpanelEvents.userShowOnlyFindingClear, {
        estimatedVisitId: estimatedVisitId,
        patientId,
      });
    } else {
      // Set this finding as selected
      setSelectedFindingImageId(currentImage);
      setSelectedFindingBounds({
        id: finding.id,
        xMin: finding.xMin,
        yMin: finding.yMin,
        xMax: finding.xMax,
        yMax: finding.yMax,
        x: finding.x,
        y: finding.y,
        type: finding.rblType ? 'rbl' : finding.type,
        tooth: finding.rblType ? tooth : finding.toothNumber,
      });
      trackEvent(mixpanelEvents.userShowOnlyFindingClick, {
        estimatedVisitId: estimatedVisitId,
        patientId,
      });
    }
  };

  return (
    <Button
      onClick={handleClick}
      className="vai-flex vai-items-center vai-gap-2 vai-bg-[#27272A] hover:vai-bg-[#3A3A3C] vai-text-white vai-text-xs vai-font-bold vai-p-2 vai-mt-2 vai-normal-case"
    >
      <FontAwesomeIcon icon={faArrowsToDot} />
      {isCurrentlySelected ? (
        <Trans>Clear Finding</Trans>
      ) : (
        <Trans>Show Only This Finding</Trans>
      )}
    </Button>
  );
};
